import { isValid } from 'clients/Clients';
import { useCart } from 'context/Cart/CartContext';
import { useCallback, useEffect, useState } from 'react';
import debounce from 'utils/debounce';
import useCartQuantity from 'zustand-lib/useCartQuantity';

const UPDATE = 'update';
const REMOVE = 'remove';
const REGEX_NUMBER = new RegExp('^[0-9]+$');

const useCartControl = (product) => {
  const { sku, consumedMaxQuantity } = product;
  const [currentValue, setCurrentValue] = useState();
  const { setCount, count } = useCartQuantity((state) => state);
  const { cartItems = [], removeCartItem, updateCartItem } = useCart();

  const updateCart = async (q, isReload = true) => {
    const response = await updateCartItem({ product, q: parseFloat(q) }, isReload);
    if (!isValid(response)) {
      setCount(product.quantity);
      setCurrentValue(product.quantity);
    }
  };

  const handleCart = (val, updateType) => {
    switch (updateType) {
      case UPDATE:
        updateCart(val);
        break;
      case REMOVE:
        removeCartItem(product, true);
        break;
      default:
        break;
    }
  };

  const handler = useCallback(
    debounce((val, updateType) => handleCart(val, updateType), 300),
    [product],
  );

  const handleDecrease = async () => {
    const newValue = count - 1;
    if (newValue < 0) {
      return null;
    }
    if (newValue === 0) {
      handler(0, REMOVE);
      return setCount(0);
    }
    handler(newValue, UPDATE);
    return setCount(newValue);
  };

  const handleIncrease = () => {
    const newValue = count + 1;

    if (newValue > consumedMaxQuantity?.maxQuantityConsumed) {
      handler(consumedMaxQuantity?.maxQuantityConsumed, UPDATE);
      setCount(consumedMaxQuantity?.maxQuantityConsumed);
    } else {
      handler(newValue, UPDATE);
      setCount(newValue);
    }
  };

  const handleBlurInput = () => {
    if (count === currentValue) {
      return null;
    }
    if (count > 0) {
      return handler(count, UPDATE);
    }
    setCurrentValue(0);
    return handler(0, REMOVE);
  };

  const handleChangeInput = async (e) => {
    const { value } = e.target;
    if (value && REGEX_NUMBER.test(value)) {
      const convertValue = await Number(value.replace(/^0+/, ''));
      if (convertValue < 0) setCount(0);
      else if (convertValue < consumedMaxQuantity?.maxQuantityConsumed) {
        setCount(convertValue);
      } else {
        setCount(consumedMaxQuantity?.maxQuantityConsumed);
      }
    } else {
      setCount(0);
    }
  };

  const loadScore = (cartItemQuantity) => {
    const productQuantity = product.quantity || 0;
    if (cartItemQuantity !== undefined && cartItemQuantity !== productQuantity) {
      setCurrentValue(cartItemQuantity);
      return cartItemQuantity;
    }
    setCurrentValue(productQuantity);
    return productQuantity;
  };

  useEffect(() => {
    const cartItemQuantity = cartItems?.find((item) => item.sku === sku)?.quantity;
    const value = loadScore(cartItemQuantity);
    setCount(value);
  }, [sku]);

  return [handleIncrease, handleBlurInput, handleChangeInput, handleDecrease, currentValue];
};

export default useCartControl;
